/**
 * Geocode Helpers
 * 
 * Utilities for improving geocoding accuracy:
 * - Country code mapping from destination strings
 * - Proximity coordinate resolution
 * - Result validation
 */

// ISO 3166-1 alpha-2 country code mappings for common destinations
const COUNTRY_CODE_MAP: Record<string, string> = {
  // Countries
  'iceland': 'is',
  'japan': 'jp',
  'south korea': 'kr',
  'korea': 'kr',
  'indonesia': 'id',
  'bali': 'id',
  'canada': 'ca',
  'france': 'fr',
  'italy': 'it',
  'united kingdom': 'gb',
  'uk': 'gb',
  'england': 'gb',
  'scotland': 'gb',
  'wales': 'gb',
  'spain': 'es',
  'portugal': 'pt',
  'germany': 'de',
  'netherlands': 'nl',
  'belgium': 'be',
  'switzerland': 'ch',
  'austria': 'at',
  'greece': 'gr',
  'turkey': 'tr',
  'thailand': 'th',
  'vietnam': 'vn',
  'singapore': 'sg',
  'malaysia': 'my',
  'philippines': 'ph',
  'australia': 'au',
  'new zealand': 'nz',
  'mexico': 'mx',
  'usa': 'us',
  'united states': 'us',
  'brazil': 'br',
  'argentina': 'ar',
  'peru': 'pe',
  'colombia': 'co',
  'chile': 'cl',
  'ecuador': 'ec',
  'egypt': 'eg',
  'morocco': 'ma',
  'south africa': 'za',
  'kenya': 'ke',
  'tanzania': 'tz',
  'uganda': 'ug',
  'rwanda': 'rw',
  'ethiopia': 'et',
  'india': 'in',
  'china': 'cn',
  'hong kong': 'hk',
  'taiwan': 'tw',
  'uae': 'ae',
  'united arab emirates': 'ae',
  'dubai': 'ae',
  'qatar': 'qa',
  'israel': 'il',
  'jordan': 'jo',
  'croatia': 'hr',
  'czech republic': 'cz',
  'czechia': 'cz',
  'poland': 'pl',
  'hungary': 'hu',
  'denmark': 'dk',
  'sweden': 'se',
  'norway': 'no',
  'finland': 'fi',
  'ireland': 'ie',
  'russia': 'ru',
  'ukraine': 'ua',

  // Major cities mapped to their countries
  'tokyo': 'jp',
  'kyoto': 'jp',
  'osaka': 'jp',
  'seoul': 'kr',
  'paris': 'fr',
  'rome': 'it',
  'london': 'gb',
  'barcelona': 'es',
  'madrid': 'es',
  'berlin': 'de',
  'amsterdam': 'nl',
  'brussels': 'be',
  'prague': 'cz',
  'vienna': 'at',
  'zurich': 'ch',
  'lisbon': 'pt',
  'athens': 'gr',
  'istanbul': 'tr',
  'bangkok': 'th',
  'ho chi minh': 'vn',
  'hanoi': 'vn',
  'ha noi': 'vn', // diacritic-normalized "Hà Nội"
  'da nang': 'vn',
  'hoi an': 'vn',
  'nha trang': 'vn',
  'trang tien': 'vn', // Tràng Tiền (Hanoi)
  'kuala lumpur': 'my',
  'manila': 'ph',
  'sydney': 'au',
  'melbourne': 'au',
  'auckland': 'nz',
  'new york': 'us',
  'new york city': 'us',
  nyc: 'us',
  brooklyn: 'us',
  'los angeles': 'us',
  'san francisco': 'us',
  'miami': 'us',
  'mexico city': 'mx',
  'cancun': 'mx',
  'sao paulo': 'br',
  'rio de janeiro': 'br',
  'buenos aires': 'ar',
  'lima': 'pe',
  'bogota': 'co',
  'cartagena': 'co',
  'medellin': 'co',
  'cairo': 'eg',
  'marrakech': 'ma',
  'cape town': 'za',
  'nairobi': 'ke',
  'entebbe': 'ug',
  'kampala': 'ug',
  'kigali': 'rw',
  'ottawa': 'ca',
  'delhi': 'in',
  'mumbai': 'in',
  'beijing': 'cn',
  'shanghai': 'cn',
  'reykjavik': 'is',
  'ubud': 'id',
  'seminyak': 'id',
  'banff': 'ca',
  'jasper': 'ca',
  'vancouver': 'ca',
  'toronto': 'ca',
  'montreal': 'ca',
  'edmonton': 'ca',
  'calgary': 'ca',
  'winnipeg': 'ca',
  'halifax': 'ca',
  'florence': 'it',
  'venice': 'it',
  'milan': 'it',
  'versailles': 'fr',
  'nice': 'fr',
  'lyon': 'fr',
  'windsor': 'gb',
  'oxford': 'gb',
  'edinburgh': 'gb',
  'dublin': 'ie',
  'copenhagen': 'dk',
  'stockholm': 'se',
  'oslo': 'no',
  'helsinki': 'fi',
  'nara': 'jp',
  'hakone': 'jp',
  'nikko': 'jp',
};

/**
 * Strip diacritics so "Hà Nội" -> "ha noi" for country/destination lookup.
 * Ensures Vietnamese (and other accented) destinations match COUNTRY_CODE_MAP.
 */
const stripDiacritics = (s: string): string => {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim();
};

// Known coordinates for major destinations for proximity bias
const normalizeDestinationForLookup = (destination: string): string => {
  let normalized = destination.toLowerCase().trim();

  // Strip diacritics so "Hà Nội" -> "ha noi", "Tràng Tiền" -> "trang tien"
  normalized = stripDiacritics(normalized);

  // Strip template-style suffixes, e.g. "Egypt 7-Day Highlights" -> "Egypt"
  normalized = normalized
    // NOTE: use real word boundaries. Some earlier edits introduced a non-printable backspace char
    // which broke these regexes and caused lookups like "Egypt 7-Day Highlights" to fail.
    .replace(/\b\d+\s*-?\s*day\b.*$/i, '')
    .replace(/\b\d+\b.*$/i, '')
    .replace(/\b(highlights?|itinerary|template|trip)\b.*$/i, '')
    .trim();

  // Normalize separators
  normalized = normalized.replace(/\s{2,}/g, ' ');

  // Trip titles like "NYC World Cup Weekend" should anchor to Manhattan, not fail lookup.
  if (/\bnyc\b/.test(normalized)) {
    normalized = 'new york';
  }

  if (/\bchi\s*town\b/.test(normalized)) {
    normalized = 'chicago';
  }

  return normalized;
};

const DESTINATION_COORDINATES: Record<string, { lat: number; lng: number }> = {
  // Iceland
  'iceland': { lat: 64.9631, lng: -19.0208 },
  'reykjavik': { lat: 64.1466, lng: -21.9426 },
  
  // Japan
  // NOTE: Tokyo Station coords, not the broader "Tokyo metropolis" centroid (which lands
  // in Suginami-ku / western suburbs and pulls landmark tilequery into residential streets).
  'tokyo': { lat: 35.6812, lng: 139.7671 },
  'kyoto': { lat: 35.0116, lng: 135.7681 },
  'osaka': { lat: 34.6937, lng: 135.5023 },
  
  // South Korea
  'seoul': { lat: 37.5665, lng: 126.9780 },
  
  // Europe
  'london': { lat: 51.5074, lng: -0.1278 },
  'paris': { lat: 48.8566, lng: 2.3522 },
  'rome': { lat: 41.9028, lng: 12.4964 },
  // Country-level fallbacks (map to capital/primary city). This is critical for templates/multi-city
  // trips where a destination "city" is stored as a country name (e.g. "France").
  'france': { lat: 48.8566, lng: 2.3522 }, // Paris
  'italy': { lat: 41.9028, lng: 12.4964 }, // Rome
  'united kingdom': { lat: 51.5074, lng: -0.1278 }, // London
  'uk': { lat: 51.5074, lng: -0.1278 }, // London
  'england': { lat: 51.5074, lng: -0.1278 }, // London
  
  // Indonesia
  'bali': { lat: -8.3405, lng: 115.0920 },
  'ubud': { lat: -8.5069, lng: 115.2625 },
  
  // Canada
  'banff': { lat: 51.1784, lng: -115.5708 },
  'canadian rockies': { lat: 51.4968, lng: -115.9281 },
  'ottawa': { lat: 45.4215, lng: -75.6972 },
  'edmonton': { lat: 53.5461, lng: -113.4938 },
  'calgary': { lat: 51.0447, lng: -114.0719 },
  'vancouver': { lat: 49.2827, lng: -123.1207 },
  'toronto': { lat: 43.6532, lng: -79.3832 },
  'montreal': { lat: 45.5017, lng: -73.5673 },
  
  // United States
  'chicago': { lat: 41.8781, lng: -87.6298 },
  'new york': { lat: 40.7128, lng: -74.0060 },
  'new york city': { lat: 40.7128, lng: -74.0060 },
  /** Common trip-name / airport shorthand */
  nyc: { lat: 40.7128, lng: -74.0060 },
  /** Borough centroid — improves proximity when templates store "Brooklyn" without NYC parent. */
  brooklyn: { lat: 40.6782, lng: -73.9442 },
  'wicker park': { lat: 41.9088, lng: -87.6796 },
  'logan square': { lat: 41.9231, lng: -87.7093 },
  'grant park': { lat: 41.8721, lng: -87.6189 },
  'west loop': { lat: 41.8823, lng: -87.6630 },
  
  // East Africa
  'uganda': { lat: 1.3733, lng: 32.2903 },
  'rwanda': { lat: -1.9403, lng: 29.8739 },
  'kigali': { lat: -1.9706, lng: 30.1044 },

  // North Africa
  'egypt': { lat: 26.8206, lng: 30.8025 },
  'cairo': { lat: 30.0444, lng: 31.2357 },
  /** Medina anchor — ensures landmark fetch / fallback center for Marrakech trips. */
  'marrakech': { lat: 31.6258, lng: -7.9892 },
  'marrakesh': { lat: 31.6258, lng: -7.9892 },

  // Colombia
  'bogota': { lat: 4.7110, lng: -74.0721 },
  'cartagena': { lat: 10.3910, lng: -75.4794 },
  'medellin': { lat: 6.2476, lng: -75.5658 },

  // Peru (Plaza Mayor / historic center; Mapbox geocoding for "Lima" sometimes returns
  // a southern admin centroid, which pulled landmark pins into Chorrillos).
  'peru': { lat: -12.0464, lng: -77.0428 }, // Lima
  'lima': { lat: -12.0464, lng: -77.0428 },
  'cusco': { lat: -13.5319, lng: -71.9675 },
  'cuzco': { lat: -13.5319, lng: -71.9675 },

  // Vietnam (so Vietnam/Hanoi trips don't fall back to user location or wrong center when geocoding fails)
  'vietnam': { lat: 21.0285, lng: 105.8542 },   // Hanoi
  'hanoi': { lat: 21.0285, lng: 105.8542 },
  'ha noi': { lat: 21.0285, lng: 105.8542 },
  'ho chi minh city': { lat: 10.8231, lng: 106.6297 },
  'saigon': { lat: 10.8231, lng: 106.6297 },
};

/**
 * Extract country code from a destination string
 * Handles various formats:
 * - "Seoul, South Korea" -> "kr"
 * - "Iceland" -> "is"
 * - "London, Rome & Paris" -> "gb" (returns first match)
 * - "Canadian Rockies, Canada" -> "ca"
 */
export const getCountryCodeFromDestination = (destination: string): string | undefined => {
  const normalized = normalizeDestinationForLookup(destination);
  
  // Direct lookup
  if (COUNTRY_CODE_MAP[normalized]) {
    return COUNTRY_CODE_MAP[normalized];
  }
  
  // Try to find a match within the destination string
  const words = normalized.split(/[,&\s]+/).filter(w => w.length > 2);
  
  for (const word of words) {
    if (COUNTRY_CODE_MAP[word]) {
      return COUNTRY_CODE_MAP[word];
    }
  }
  
  // Try to find partial matches (for compound names like "South Korea")
  for (const [key, code] of Object.entries(COUNTRY_CODE_MAP)) {
    if (normalized.includes(key)) {
      return code;
    }
  }
  
  return undefined;
};

/**
 * Get country code for a specific day's location in a multi-city trip
 * Analyzes the activity's location to determine which country it's in
 */
export const getCountryCodeForActivity = (
  activityLocation: string | undefined,
  templateDestination: string
): string | undefined => {
  // First try to get country from the activity's specific location
  if (activityLocation) {
    const activityCountry = getCountryCodeFromDestination(activityLocation);
    if (activityCountry) {
      return activityCountry;
    }
  }
  
  // Fall back to template destination
  return getCountryCodeFromDestination(templateDestination);
};

/**
 * Get coordinates for a destination to use as proximity bias
 */
export const getDestinationCoordinates = (destination: string): { lat: number; lng: number } | undefined => {
  const normalized = normalizeDestinationForLookup(destination);
  
  // Direct lookup
  if (DESTINATION_COORDINATES[normalized]) {
    if (__DEV__) {
      console.log(`[GeocodeHelpers] Found direct match for destination: "${destination}" -> ${DESTINATION_COORDINATES[normalized].lat}, ${DESTINATION_COORDINATES[normalized].lng}`);
    }
    return DESTINATION_COORDINATES[normalized];
  }
  
  // Try to find a match within the destination string
  // Split by comma, ampersand, or whitespace, and filter out short words
  const words = normalized.split(/[,&\s]+/).filter(w => w.length > 2);
  
  for (const word of words) {
    if (DESTINATION_COORDINATES[word]) {
      if (__DEV__) {
        console.log(`[GeocodeHelpers] Found word match for destination: "${destination}" (word: "${word}") -> ${DESTINATION_COORDINATES[word].lat}, ${DESTINATION_COORDINATES[word].lng}`);
      }
      return DESTINATION_COORDINATES[word];
    }
  }
  
  // Try partial match (e.g., "south korea" should match "seoul" if it's in the context)
  // This handles cases like "Seoul, South Korea" where we want to match "seoul"
  for (const [key, coords] of Object.entries(DESTINATION_COORDINATES)) {
    if (normalized.includes(key)) {
      if (__DEV__) {
        console.log(`[GeocodeHelpers] Found partial match for destination: "${destination}" (contains: "${key}") -> ${coords.lat}, ${coords.lng}`);
      }
      return coords;
    }
  }
  
  if (__DEV__) {
    console.warn(`[GeocodeHelpers] Could not find coordinates for destination: "${destination}" (normalized: "${normalized}", words: [${words.join(', ')}])`);
  }
  return undefined;
};

export type HaversineCoord =
  | { lat: number; lng: number }
  | { latitude: number; longitude: number };

const toLatLng = (c: HaversineCoord): { lat: number; lng: number } =>
  'latitude' in c ? { lat: c.latitude, lng: c.longitude } : { lat: c.lat, lng: c.lng };

/**
 * Calculate haversine distance between two coordinates in kilometers
 */
export const haversineDistance = (coord1: HaversineCoord, coord2: HaversineCoord): number => {
  const p = toLatLng(coord1);
  const q = toLatLng(coord2);
  const R = 6371; // Earth's radius in km
  const dLat = ((q.lat - p.lat) * Math.PI) / 180;
  const dLng = ((q.lng - p.lng) * Math.PI) / 180;
  
  const hav =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((p.lat * Math.PI) / 180) *
      Math.cos((q.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(hav), Math.sqrt(1 - hav));
  
  return R * c;
};

export type LatLngPoint = { latitude: number; longitude: number };

/**
 * Morocco is a single Mapbox `country=ma` region; wrong Firestore coordinates on a destination row,
 * or street names like "Rue Marrakech" elsewhere, can bias proximity to the wrong end of the country.
 * When copy clearly refers to the Marrakech–Palmeraie area, prefer its centroid as the proximity
 * anchor whenever the resolved `destCenter` lies far from that area (>110km).
 */
export const marrakechAreaProximityOverrideForMorocco = (params: {
  countryCode: string | undefined | null;
  destCenter: LatLngPoint | null;
  tripName?: string | null;
  destinationLabel?: string | null;
  destinationCity?: string | null;
  address?: string | null;
  location?: string | null;
  title?: string | null;
}): LatLngPoint | null => {
  if (`${params.countryCode || ''}`.toLowerCase() !== 'ma') return null;

  const hay = [
    params.tripName,
    params.destinationLabel,
    params.destinationCity,
    params.address,
    params.location,
    params.title,
  ]
    .map((s) => `${s || ''}`.toLowerCase())
    .join(' ');

  const marrakechIntent =
    /\b(marrakech|marrakesh|palmeraie|al\s*palmeraie|circuit\s+de\s+la\s+palmeraie)\b/.test(hay) ||
    /\b40000\b/.test(hay);

  if (!marrakechIntent) return null;

  const mk = getDestinationCoordinates('marrakech');
  if (!mk) return null;

  const anchor: LatLngPoint = { latitude: mk.lat, longitude: mk.lng };
  if (!params.destCenter) return anchor;

  const km = haversineDistance(
    { lat: params.destCenter.latitude, lng: params.destCenter.longitude },
    anchor,
  );
  if (km > 110) return anchor;
  return null;
};

/** Rough ISO country bounding boxes for validating geocodes when no city table entry exists. */
export const COUNTRY_BBOX: Record<
  string,
  { minLat: number; maxLat: number; minLng: number; maxLng: number }
> = {
  ca: { minLat: 41.5, maxLat: 83.5, minLng: -141.0, maxLng: -52.0 },
  us: { minLat: 18.0, maxLat: 72.0, minLng: -180.0, maxLng: -64.0 },
  mx: { minLat: 14.0, maxLat: 33.0, minLng: -118.5, maxLng: -86.0 },
  gb: { minLat: 49.5, maxLat: 61.0, minLng: -8.5, maxLng: 2.0 },
  fr: { minLat: 41.0, maxLat: 51.5, minLng: -5.5, maxLng: 10.0 },
  de: { minLat: 47.0, maxLat: 55.5, minLng: 5.5, maxLng: 15.5 },
  it: { minLat: 36.0, maxLat: 47.5, minLng: 6.5, maxLng: 19.0 },
  es: { minLat: 36.0, maxLat: 44.0, minLng: -10.0, maxLng: 5.0 },
  au: { minLat: -44.0, maxLat: -9.0, minLng: 112.0, maxLng: 154.0 },
  nz: { minLat: -47.5, maxLat: -34.0, minLng: 166.0, maxLng: 179.0 },
  jp: { minLat: 24.0, maxLat: 46.5, minLng: 122.0, maxLng: 146.0 },
  kr: { minLat: 33.0, maxLat: 39.0, minLng: 124.0, maxLng: 132.0 },
  in: { minLat: 6.0, maxLat: 36.0, minLng: 68.0, maxLng: 98.0 },
  cn: { minLat: 18.0, maxLat: 54.0, minLng: 73.0, maxLng: 135.0 },
  br: { minLat: -34.0, maxLat: 6.0, minLng: -74.0, maxLng: -34.0 },
  ar: { minLat: -55.5, maxLat: -21.5, minLng: -73.5, maxLng: -53.0 },
  eg: { minLat: 22.0, maxLat: 32.0, minLng: 24.5, maxLng: 37.0 },
  ma: { minLat: 21.0, maxLat: 36.0, minLng: -17.5, maxLng: -0.5 },
  vn: { minLat: 8.0, maxLat: 24.0, minLng: 102.0, maxLng: 110.0 },
  th: { minLat: 5.0, maxLat: 21.0, minLng: 97.0, maxLng: 106.0 },
  id: { minLat: -11.0, maxLat: 6.5, minLng: 95.0, maxLng: 141.0 },
  is: { minLat: 63.0, maxLat: 67.5, minLng: -25.0, maxLng: -13.0 },
  ie: { minLat: 51.0, maxLat: 55.5, minLng: -11.0, maxLng: -5.5 },
  nl: { minLat: 50.5, maxLat: 53.7, minLng: 3.0, maxLng: 7.5 },
  ch: { minLat: 45.5, maxLat: 48.0, minLng: 5.5, maxLng: 10.5 },
  at: { minLat: 46.0, maxLat: 49.5, minLng: 9.0, maxLng: 17.5 },
  pt: { minLat: 36.5, maxLat: 42.5, minLng: -10.0, maxLng: -6.0 },
  gr: { minLat: 34.5, maxLat: 42.0, minLng: 19.0, maxLng: 30.0 },
  tr: { minLat: 36.0, maxLat: 42.5, minLng: 26.0, maxLng: 45.0 },
  ae: { minLat: 22.5, maxLat: 26.5, minLng: 51.0, maxLng: 57.0 },
  za: { minLat: -35.0, maxLat: -22.0, minLng: 16.0, maxLng: 33.0 },
  ke: { minLat: -5.0, maxLat: 5.5, minLng: 33.5, maxLng: 42.0 },
  ug: { minLat: -1.5, maxLat: 4.5, minLng: 29.5, maxLng: 35.5 },
  rw: { minLat: -3.0, maxLat: -1.0, minLng: 28.5, maxLng: 31.0 },
  pe: { minLat: -19.0, maxLat: 0.0, minLng: -82.0, maxLng: -68.0 },
  co: { minLat: -4.5, maxLat: 13.5, minLng: -79.5, maxLng: -66.5 },
};

export const COUNTRY_NAME_VARIANTS: Record<string, string[]> = {
  ca: ['canada', 'canadian'],
  us: ['united states', 'usa', 'america', 'u.s.a.'],
  uk: ['united kingdom', 'britain', 'england', 'scotland', 'wales'],
  fr: ['france', 'french'],
  de: ['germany', 'german'],
  it: ['italy', 'italian'],
  es: ['spain', 'spanish'],
  au: ['australia', 'australian'],
  nz: ['new zealand'],
  jp: ['japan', 'japanese'],
  kr: ['korea', 'south korea', 'korean'],
  vn: ['vietnam', 'vietnamese'],
  ng: ['nigeria', 'nigerian'],
  ma: ['morocco', 'moroccan'],
  mx: ['mexico', 'mexican'],
  br: ['brazil', 'brazilian'],
  in: ['india', 'indian'],
  cn: ['china', 'chinese'],
};

/**
 * Parse "City, Country" or "City, ST, Country" from a destination string.
 */
export function parseCityCountryFromLabel(raw: string): { city: string; country?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { city: '' };

  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 1) return { city: parts[0] };

  const city = parts[0];
  const last = parts[parts.length - 1];
  if (getCountryCodeFromDestination(last)) {
    return { city, country: last };
  }

  return { city };
}

export type ResolvedTripDestinationLabel = {
  label: string;
  city: string;
  country?: string;
  countryCode?: string;
};

/**
 * Build a geocodable trip destination label, preserving country from structured rows or legacy strings.
 */
export function resolveTripDestinationLabel(
  destination?: { city?: string; country?: string; countryCode?: string } | null,
  fallback?: string,
): ResolvedTripDestinationLabel | null {
  let city = destination?.city?.trim() || '';
  let country = destination?.country?.trim();

  const fallbackParsed = fallback?.trim() ? parseCityCountryFromLabel(fallback) : null;
  if (!city && fallbackParsed?.city) {
    city = fallbackParsed.city;
  }
  if (!country && fallbackParsed?.country) {
    country = fallbackParsed.country;
  }
  if (!city) {
    city = fallback?.split(',')[0]?.trim() || fallback?.trim() || '';
  }

  if (!city) return null;

  const label = country ? `${city}, ${country}` : city;
  const countryCode =
    destination?.countryCode ||
    getCountryCodeFromDestination(label) ||
    getCountryCodeFromDestination(city) ||
    (country ? getCountryCodeFromDestination(country) : undefined);

  return { label, city, country, countryCode };
}

/** Append country context to a bare city label when hints are available. */
export function enrichCityLabelWithCountryParts(
  cityLabel: string,
  hints: { country?: string; fullDestination?: string },
): string {
  const trimmed = cityLabel.trim();
  if (!trimmed || trimmed.includes(',')) return trimmed;

  if (hints.country?.trim()) {
    return `${trimmed}, ${hints.country.trim()}`;
  }

  if (hints.fullDestination?.trim()) {
    const parsed = parseCityCountryFromLabel(hints.fullDestination);
    if (parsed.country && parsed.city.toLowerCase() === trimmed.toLowerCase()) {
      return `${trimmed}, ${parsed.country}`;
    }
  }

  return trimmed;
}

export function coordsWithinCountryBounds(
  countryCode: string | undefined,
  lat: number,
  lng: number,
): boolean {
  if (!countryCode) return true;
  const bbox = COUNTRY_BBOX[countryCode.toLowerCase()];
  if (!bbox) return true;
  return (
    lat >= bbox.minLat &&
    lat <= bbox.maxLat &&
    lng >= bbox.minLng &&
    lng <= bbox.maxLng
  );
}

export function geocodeContextMatchesCountryCode(
  context: Array<{ id?: string; text?: string }> | undefined,
  countryCode: string | undefined,
): boolean {
  if (!countryCode) return true;
  const resultCountry = context
    ?.find((ctx) => ctx.id?.startsWith('country'))
    ?.text?.toLowerCase();
  if (!resultCountry) return true;

  const expectedCountries = COUNTRY_NAME_VARIANTS[countryCode.toLowerCase()] || [];
  if (expectedCountries.length === 0) return true;

  return expectedCountries.some((name) => resultCountry.includes(name));
}

/**
 * Validate that a geocoded result is within a reasonable distance of the expected destination
 */
export const validateGeocodeResult = (
  resultCoordinates: { lat: number; lng: number },
  expectedDestination: string,
  maxDistanceKm: number = 500
): boolean => {
  const expectedCoords = getDestinationCoordinates(expectedDestination);

  if (expectedCoords) {
    const distance = haversineDistance(resultCoordinates, expectedCoords);
    return distance <= maxDistanceKm;
  }

  const countryCode = getCountryCodeFromDestination(expectedDestination);
  if (countryCode) {
    return coordsWithinCountryBounds(countryCode, resultCoordinates.lat, resultCoordinates.lng);
  }

  return false;
};

/**
 * Build a better search string by appending destination context to vague locations
 * Useful when the address field is empty or too generic
 */
/**
 * Long administrative strings (e.g. "Brooklyn, Kings County, City of New York, NY")
 * geocode poorly as venue queries; prefer pairing the activity title with the trip city instead.
 */
export function isLikelyAdministrativeRegionQuery(text: string): boolean {
  const t = text.trim();
  if (t.length < 12) return false;
  const commaCount = (t.match(/,/g) || []).length;
  if (commaCount < 2) return false;
  const lower = t.toLowerCase();
  if (/\b(county|borough|prefecture|municipality|parish|community board)\b/.test(lower)) return true;
  if (/\bcity of\b/.test(lower) && commaCount >= 2) return true;
  const first = t.split(',')[0]?.trim() || '';
  if (commaCount >= 3 && !/^\d+\s/.test(first) && t.length > 40) return true;
  return false;
}

/**
 * Pull a coarse "City, ST" hint from OSM-style admin strings used in template addresses.
 * Example: "Upper West Side, Manhattan … New York, 10024, United States" → "New York, NY"
 */
export function extractMetroHintFromAdministrativeAddress(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  const stateZipMatch = trimmed.match(
    /,\s*([A-Za-z .'-]+),\s*([A-Z]{2})\s*(?:,?\s*\d{5}(?:-\d{4})?)?\s*(?:,?\s*(?:United States|USA))?\s*$/i,
  );
  if (stateZipMatch) {
    const state = stateZipMatch[2].toUpperCase();
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const part = parts[i];
      if (/^city of new york$/i.test(part)) return `New York, ${state}`;
      if (/^(manhattan|brooklyn|queens|bronx|staten island)\b/i.test(part)) return `New York, ${state}`;
    }
    const cityPart = stateZipMatch[1]
      .replace(/\bcounty\b/gi, '')
      .replace(/\bcommunity board \d+\b/gi, '')
      .trim();
    if (cityPart && !/^\d/.test(cityPart)) {
      return `${cityPart}, ${state}`;
    }
  }

  if (/\b(manhattan|brooklyn|queens|bronx|staten island|new york city)\b/i.test(lower)) {
    return 'New York, NY';
  }

  return null;
}

const CITY_HINT_EXCLUDE = new Set([
  'canada',
  'united states',
  'usa',
  'uk',
  'united kingdom',
  'ontario',
  'quebec',
  'alberta',
  'british columbia',
  'newfoundland and labrador',
]);

export const normalizeForCityHintMatch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Extract an explicit city hint from a user-provided address string.
 * Example: "Yonge Street, Toronto, Ontario M4W 3V8, Canada" -> "toronto"
 */
export function extractExplicitCityHintFromAddress(address: string): string | null {
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;

  const raw = parts[1];
  if (!raw || raw.length < 2) return null;
  if (/\d/.test(raw)) return null;

  const normalized = normalizeForCityHintMatch(raw);
  if (!normalized || CITY_HINT_EXCLUDE.has(normalized)) return null;
  return normalized;
}

export function featureMatchesCityHintInContext(feature: any, cityHint: string | null): boolean {
  if (!cityHint) return true;
  const contextItems = Array.isArray(feature?.context) ? feature.context : [];
  const contextText = contextItems
    .map((ctx: any) => (typeof ctx?.text === 'string' ? normalizeForCityHintMatch(ctx.text) : ''))
    .filter(Boolean)
    .join(' ');
  if (!contextText) return false;
  return contextText.includes(cityHint);
}

export const buildContextualSearchString = (
  location: string,
  address: string | undefined,
  destinationCity: string,
  destinationCountry?: string
): string => {
  const baseRaw = (address || location || '').trim();
  if (!baseRaw) return baseRaw;

  const baseLower = baseRaw.toLowerCase();
  const cityLower = (destinationCity || '').toLowerCase().trim();
  const countryLower = (destinationCountry || '').toLowerCase().trim();

  // If the base already contains obvious place context, don't mutate it.
  // (But do NOT assume "long string" == "complete address": many users paste street-only lines.)
  const hasPlaceContext =
    baseLower.includes(',') ||
    (cityLower && baseLower.includes(cityLower)) ||
    (countryLower && baseLower.includes(countryLower)) ||
    /\b(japan|korea|france|italy|united kingdom|britain|uk|iceland|indonesia|canada|united states|usa|america|spain|germany)\b/i.test(baseRaw);

  if (hasPlaceContext) {
    return baseRaw;
  }

  // Build a more specific search string by appending destination context.
  let searchString = baseRaw;

  if (destinationCity && !baseLower.includes(cityLower)) {
    searchString += `, ${destinationCity}`;
  }

  if (destinationCountry && !baseLower.includes(countryLower)) {
    searchString += `, ${destinationCountry}`;
  }

  return searchString;
};

export function canonicalCityHintForAddressMatch(hint: string): string[] {
  const h = hint.trim().toLowerCase();
  if (!h) return [];
  const variants = new Set<string>([h]);
  if (h === 'marrakech') {
    variants.add('marrakesh');
  } else if (h === 'marrakesh') {
    variants.add('marrakech');
  }
  return [...variants];
}

export function addressContainsCityHint(addressLower: string, locHintRaw: string): boolean {
  const hint = locHintRaw.trim().toLowerCase();
  if (!hint) return true;
  for (const v of canonicalCityHintForAddressMatch(hint)) {
    if (addressLower.includes(v)) return true;
  }
  return false;
}

/**
 * True when the address line already looks like a geocodable postal/street address, so appending
 * a venue/floor label from `ItineraryItem.location` usually confuses providers (templates often put
 * the place title there while the real street line is in `address`).
 */
export function addressLooksStructuredForGeocoding(address: string): boolean {
  const t = address.trim();
  if (t.length < 12) return false;
  const commas = (t.match(/,/g) || []).length;
  if (commas >= 2) return true;
  if (commas >= 1 && /^\d+\s/.test(t)) return true;
  if (/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/i.test(t)) return true;
  if (/\b\d{5}(-\d{4})?\b/.test(t)) return true;
  return false;
}

export type ItineraryGeocodeTextSource = {
  address?: string | null;
  location?: string | null;
  title?: string | null;
};

/**
 * Text sent to the geocoder (map pins) and mirrored for external map links.
 * Edit form labels `location` as "City (optional)"; merging it with `address` is for PO boxes /
 * short lines — not for venue titles copied from templates.
 */
export function buildItineraryGeocodeQueryText(item: ItineraryGeocodeTextSource): string {
  const address = (item.address || '').trim();
  const location = (item.location || '').trim();
  const title = (item.title || '').trim();

  // OSM-style admin polygons from templates geocode to wrong regional centroids.
  // Prefer activity title + neighborhood + metro hint instead of the full admin string.
  if (address && isLikelyAdministrativeRegionQuery(address)) {
    const neighborhood = address.split(',')[0]?.trim() || '';
    const metroHint = extractMetroHintFromAdministrativeAddress(address) || location || null;
    if (title && neighborhood && metroHint) {
      return `${title}, ${neighborhood}, ${metroHint}`.trim();
    }
    if (title && metroHint) {
      return `${title}, ${metroHint}`.trim();
    }
    if (title && location) {
      return `${title}, ${location}`.trim();
    }
    if (title && neighborhood) {
      return `${title}, ${neighborhood}`.trim();
    }
  }

  if (address && addressLooksStructuredForGeocoding(address)) {
    return address;
  }

  if (address && location) {
    const addrLower = address.toLowerCase();
    const locHint = location.split(',')[0].trim();
    if (locHint && !addressContainsCityHint(addrLower, locHint)) {
      return `${address}, ${location}`.trim();
    }
  }
  if (address) return address;
  if (location) return location;
  return title;
}

/**
 * Determine the current city context for a multi-city trip based on day and activities
 * This helps with geocoding when creating trips from templates
 */
export const getCurrentCityFromItinerary = (
  destination: string,
  dayIndex: number,
  activityLocations: string[]
): { city: string; country?: string } => {
  // Parse multi-city destinations like "London, Rome & Paris"
  const cities = destination.split(/[,&]/).map(s => s.trim()).filter(Boolean);
  
  if (cities.length <= 1) {
    // Single destination
    const parts = destination.split(',').map(s => s.trim());
    return {
      city: parts[0],
      country: parts[1],
    };
  }
  
  // For multi-city, try to determine current city from activity locations
  for (const loc of activityLocations) {
    for (const city of cities) {
      if (loc.toLowerCase().includes(city.toLowerCase())) {
        return { city };
      }
    }
  }
  
  // Fallback: rough estimation based on day index and number of cities
  const daysPerCity = Math.ceil(14 / cities.length); // Assuming even split
  const cityIndex = Math.min(Math.floor(dayIndex / daysPerCity), cities.length - 1);
  
  return { city: cities[cityIndex] };
};

